/**
 * Nada bicara bot per TENANT (10 Okt 2026) — MURNI, TANPA impor (pola
 * `lib/chatStyle`/`lib/llmChat`: dimuat probe Node `--experimental-strip-types`).
 *
 * Kenapa dipisah dari `lib/chatStyle`: dua hal berbeda yang sering tertukar —
 *   - `chatStyle` = VARIASI antar pesan (bergilir tiap pesan, milik sistem);
 *   - `chatTone`  = IDENTITAS cara bicara satu usaha (dipilih owner, berlaku
 *     terus sampai diubah).
 * Menggabungkan keduanya membuat "gaya bergilir" bisa diam-diam menimpa pilihan
 * owner (nada formal tiba-tiba dibalas bahasa gaul karena gaya ke-4 kebetulan
 * terpilih). Di prompt, nada DIPERLAKUKAN SEBAGAI ATURAN UTAMA dan gaya hanya
 * boleh memilih kata di dalamnya (lihat `lib/llmPrompt`, aturan presedensi).
 *
 * Referensi (audit 10 Okt 2026): tetrate.io "System Prompts: Design Patterns and
 * Best Practices" — communication style instruction (formal/casual/concise)
 * adalah elemen baku system prompt; heeya.fr "Chatbot System Prompt Engineering
 * 2026" — system prompt menetapkan tone/persona, bukan pengetahuan.
 *
 * Dipakai di:
 *   - convex/botLlm.ts        → `resolveChatToneHint(tenant.settings.tone)`
 *   - convex/dashboard.ts     → panel 🏪 Toko (baca/tulis `tenants.settings.tone`)
 *   - dashboard/src/App.tsx   → iterasi `CHAT_TONES` (label TIDAK disalin manual)
 *   - scripts/probe-chat-tone.ts → anti-regresi
 *
 * Unit test: `scripts/probe-chat-tone.ts` (blocking CI).
 */

/** Nada bicara yang bisa dipilih owner — urutan = urutan tampil di dashboard. */
export const CHAT_TONES = [
  {
    key: "santai",
    label: "Santai (bawaan)",
    emoji: "🙂",
    description: "Bahasa Indonesia sehari-hari, ramah, secukupnya — perilaku bot sebelum fitur ini ada.",
    hint: "nada santai & ramah seperti kasir warung yang akrab, tetap sopan dan tidak berlebihan",
  },
  {
    key: "formal",
    label: "Formal",
    emoji: "🎩",
    description: "Bahasa baku, tanpa kata gaul/singkatan, tanpa emoji — cocok untuk toko yang ingin terlihat rapi.",
    hint: 'nada FORMAL: pakai bahasa Indonesia baku, tanpa kata gaul/singkatan/emoji, sapa user dengan "Bapak" atau "Ibu"',
  },
  {
    key: "gaul",
    label: "Gaul",
    emoji: "😎",
    description: "Bahasa anak muda, ringan, boleh sedikit bercanda — tetap sopan, tidak kasar.",
    hint: 'nada GAUL: bahasa sehari-hari yang ringan (mis. "siap", "oke"), boleh bercanda sedikit, tapi TIDAK kasar dan TIDAK menyinggung',
  },
  {
    key: "singkat",
    label: "Singkat",
    emoji: "⚡",
    description: "Satu kalimat pendek, langsung ke inti, tanpa basa-basi pembuka.",
    hint: "nada SINGKAT: satu kalimat pendek, langsung ke inti, tanpa kalimat pembuka basa-basi",
  },
] as const;

export type ChatTone = (typeof CHAT_TONES)[number];
export type ChatToneKey = ChatTone["key"];

/** Nada yang dipakai bila tenant belum memilih (perilaku lama, tanpa perubahan). */
export const DEFAULT_CHAT_TONE: ChatToneKey = "santai";

/** Semua key sah, urut sesuai `CHAT_TONES`. */
export const CHAT_TONE_KEYS: readonly ChatToneKey[] = CHAT_TONES.map((tone) => tone.key);

/** Key ini nada yang dikenal? (dipakai validasi mutation — bukan `any`). */
export function isChatToneKey(value: unknown): value is ChatToneKey {
  return typeof value === "string" && CHAT_TONE_KEYS.includes(value as ChatToneKey);
}

/** Spec satu nada; `null` bila key tidak dikenal (arg dari luar). */
export function chatToneSpec(key: unknown): ChatTone | null {
  return isChatToneKey(key) ? (CHAT_TONES.find((tone) => tone.key === key) ?? null) : null;
}

/**
 * Key nada dari DB/nilai apa pun: tidak dikenal / kosong → `DEFAULT_CHAT_TONE`.
 * Data rusak di DB tidak boleh mematikan jalur obrolan (fail-open ke bawaan).
 */
export function normalizeChatTone(raw: unknown): ChatToneKey {
  return isChatToneKey(raw) ? raw : DEFAULT_CHAT_TONE;
}

/** Label nada untuk UI/pesan pesan error. */
export function chatToneLabel(raw: unknown): string {
  return chatToneSpec(normalizeChatTone(raw))?.label ?? DEFAULT_CHAT_TONE;
}

/**
 * Teks yang disuntik ke prompt untuk nada tenant. Selalu ada (bawaan = santai)
 * supaya bentuk prompt tidak berubah-ubah dan snapshot deploy tetap
 * deterministik.
 */
export function resolveChatToneHint(raw: unknown): string {
  return chatToneSpec(normalizeChatTone(raw))?.hint ?? CHAT_TONES[0].hint;
}

/** Pesan error Indonesia untuk input nada yang tidak dikenal (dashboard). */
export function chatToneError(raw: unknown): string {
  const keys = CHAT_TONE_KEYS.join(", ");
  return `Nada bicara "${String(raw)}" tidak dikenal. Pilih salah satu: ${keys}.`;
}
